namespace $ {
	export type $shm_hitalama_components_github_state = { branch: string, head: string, tree: string, files: Record< string, string >, private?: boolean }
	/** Client-only Git history. The live CRDT definition remains the source of truth. */
	export class $shm_hitalama_components_github extends $mol_object {
		static suggested_repository( fqn: string, owner: string ) {
			owner = owner.trim()
			if( !owner ) return ''
			if( !/^[A-Za-z0-9-]+$/.test( owner ) ) throw new Error( 'Укажите имя пользователя или организации GitHub без URL и слешей.' )
			if( !/^\$[A-Za-z][A-Za-z0-9]*_(?:[A-Za-z0-9]+_)*[A-Za-z0-9]+$/.test( fqn ) ) throw new Error( 'Укажите FQN без пустых сегментов перед сохранением на GitHub.' )
			return this.repository( owner + '/' + fqn.slice( 1 ) )
		}
		static repository( value: string ) {
			const match = /^(?:https:\/\/github\.com\/)?([A-Za-z0-9_.-]+)\/([A-Za-z0-9_.-]+?)(?:\.git)?\/?$/.exec( value.trim() )
			if( !match || [ '.', '..' ].includes( match[1] ) || [ '.', '..' ].includes( match[2] ) ) throw new Error( 'Укажите репозиторий https://github.com/владелец/репозиторий.' )
			return match[1] + '/' + match[2]
		}
		static files( definition: $shm_hitalama_map_widget_entity_custom, catalog = definition.Owner_catalog?.()?.remote() ?? null ) {
			definition = this.$.$shm_hitalama_components_owner.definition( definition, catalog )
			const root = definition.class_name()
			this.suggested_repository( root, 'owner' )
			const definitions = [ definition ]
			if( catalog ) {
				this.$.$shm_hitalama_map_component_ready.read( catalog )
				for( const key of catalog.Entries()?.keys() ?? [] ) {
					if( typeof key !== 'string' || !key.startsWith( root + '_' ) ) continue
					const child = catalog.definition( key )
					if( !child ) throw new Error( 'Не загружен внутренний модуль ' + key )
					this.$.$shm_hitalama_map_component_ready.read( child )
					if( child.class_name() !== key ) throw new Error( 'FQN внутреннего модуля не совпадает с каталогом: ' + key )
					definitions.push( child )
				}
			}
			definitions.sort( ( a, b )=> a.class_name().localeCompare( b.class_name() ) )
			const files: Record< string, string > = {}
			for( const item of definitions ) {
				const name = item.class_name(), leaf = name.split( '_' ).at( -1 )!
				this.suggested_repository( name, 'owner' )
				const directory = name === root ? '' : name.slice( root.length + 1 ).split( '_' ).join( '/' ) + '/'
				const sources = { tree: leaf + '.view.tree', logic: leaf + '.view.ts', style: leaf + '.view.css.ts', demo: 'demo.ts', default: 'default.ts', readme: 'readme.md' }
				for( const [ kind, path ] of Object.entries( sources ) ) {
					const key = kind as $shm_hitalama_map_widget_entity_custom_source_kind
					const source = item.source_link( key )
					if( source ) this.$.$shm_hitalama_map_component_ready.read( source )
					files[ directory + path ] = item.source_text( key )
				}
				files[ directory + 'dependencies.txt' ] = item.Dependencies()?.val() ?? ''
				files[ directory + 'module.json' ] = JSON.stringify({ fqn: name, title: item.display_name(), description: item.Description()?.val() ?? '', widget_type: item.widget_type() }, null, '\t' ) + '\n'
			}
			files[ '.hitalama.json' ] = JSON.stringify({ format: 2, fqn: root, modules: definitions.map( item => item.class_name() ), files: Object.keys( files ).sort() }, null, '\t' ) + '\n'
			return files
		}
		/** Only paths reserved by this exporter may be removed, never arbitrary repository files. */
		static managed_path( path: string, fqn: string ) {
			const parts = path.split( '/' ), file = parts.pop()!
			if( parts.some( part => !/^[A-Za-z0-9]+$/.test( part ) ) ) return false
			const leaf = parts.at( -1 ) ?? fqn.split( '_' ).at( -1 )!
			return [ leaf + '.view.tree', leaf + '.view.ts', leaf + '.view.css.ts', 'demo.ts', 'default.ts', 'readme.md', 'dependencies.txt', 'module.json' ].includes( file )
		}
		async blob_text( repository: string, token: string, sha: string ) {
			const blob = await this.request( repository, token, '/git/blobs/' + sha )
			if( blob.encoding !== 'base64' ) throw new Error( 'Неподдерживаемый формат метаданных GitHub.' )
			return new TextDecoder().decode( Uint8Array.from( atob( blob.content.replace( /\s/g, '' ) ), ch => ch.charCodeAt( 0 ) ) )
		}
		async metadata( repository: string, token: string, sha: string ) { return JSON.parse( await this.blob_text( repository, token, sha ) ) }
		async changes( repository: string, token: string, snapshot: string, remote: $shm_hitalama_components_github_state | null ) {
			const files = JSON.parse( snapshot ) as Record<string, string>, hashes = await this.hashes( snapshot )
			const changed = Object.keys( hashes ).filter( path => hashes[ path ] !== remote?.files[ path ] )
			const removed = new Set<string>()
			const manifest = files[ '.hitalama.json' ] ? JSON.parse( files[ '.hitalama.json' ] ) : null
			if( remote && manifest ) {
				this.$.$shm_hitalama_components_github.suggested_repository( manifest.fqn, 'owner' )
				const previous = remote.files[ '.hitalama.json' ] ? await this.metadata( repository, token, remote.files[ '.hitalama.json' ] ) : null
				if( previous ) {
					if( previous.format !== 2 || previous.fqn !== manifest.fqn || !Array.isArray( previous.files ) || !Array.isArray( previous.modules ) ) throw new Error( 'Репозиторий содержит метаданные другого приложения или неизвестного формата.' )
					const directories = new Set<string>( previous.modules.map( ( name: string )=> {
						if( typeof name !== 'string' || name !== manifest.fqn && !name.startsWith( manifest.fqn + '_' ) ) throw new Error( 'Некорректный внутренний модуль в метаданных приложения.' )
						this.$.$shm_hitalama_components_github.suggested_repository( name, 'owner' )
						return name === manifest.fqn ? '' : name.slice( manifest.fqn.length + 1 ).split( '_' ).join( '/' ) + '/'
					}) )
					for( const path of previous.files ) {
						if( typeof path !== 'string' || !directories.has( path.slice( 0, path.lastIndexOf( '/' ) + 1 ) ) || !this.$.$shm_hitalama_components_github.managed_path( path, manifest.fqn ) ) throw new Error( 'Некорректный путь в метаданных приложения.' )
						if( !( path in files ) && path in remote.files ) removed.add( path )
					}
				}
				const legacy = manifest.fqn.slice( 1 ).split( '_' ).join( '/' ) + '/'
				if( remote.files[ legacy + 'module.json' ] ) {
					const old = await this.metadata( repository, token, remote.files[ legacy + 'module.json' ] )
					if( old.fqn !== manifest.fqn ) throw new Error( 'Старый каталог экспорта принадлежит другому модулю. Автоматический перенос остановлен.' )
					for( const path of Object.keys( remote.files ) ) {
						if( !( path in files ) && path.startsWith( legacy ) && !path.slice( legacy.length ).includes( '/' ) && this.$.$shm_hitalama_components_github.managed_path( path.slice( legacy.length ), manifest.fqn ) ) removed.add( path )
					}
				}
				if( remote.files[ 'README.md' ] && 'readme.md' in files ) {
					const readme = await this.blob_text( repository, token, remote.files[ 'README.md' ] )
					if( readme.trim() !== '# ' + repository.split( '/' )[1] ) throw new Error( 'В репозитории есть README.md. Перенесите его содержимое в документацию приложения и удалите дубликат перед сохранением: README.md и readme.md конфликтуют на Windows.' )
					removed.add( 'README.md' )
				}
			}
			return { changed, removed: [...removed].sort() }
		}
		async request( repository: string, token: string, path: string, method = 'GET', data?: unknown ): Promise< any > {
			return this.api_request( '/repos/' + repository + path, token, method, data )
		}
		async api_request( path: string, token: string, method = 'GET', data?: unknown ): Promise< any > {
			if( !token.trim() ) throw new Error( 'Укажите GitHub-токен в настройках Hitalama.' )
			const response = await fetch( 'https://api.github.com' + path, {
				method, cache: 'no-store', redirect: 'error', signal: AbortSignal.timeout( 20000 ),
				headers: { Accept: 'application/vnd.github+json', Authorization: 'Bearer ' + token.trim(), 'X-GitHub-Api-Version': '2022-11-28', ...( data === undefined ? {} : { 'Content-Type': 'application/json' } ) },
				body: data === undefined ? undefined : JSON.stringify( data ),
			}).catch( ()=> { throw new Error( 'Не удалось связаться с GitHub. Проверьте подключение и повторите проверку.' ) } )
			if( !response.ok ) {
				const messages: Record< number, string > = { 401: 'Проверьте GitHub-токен в настройках.', 403: 'Недостаточно прав или исчерпан лимит GitHub.', 404: 'Репозиторий или ветка не найдены. Проверьте адрес и доступ токена.', 409: 'Репозиторий изменился. Обновите проверку перед коммитом.', 422: 'GitHub отклонил коммит. Обновите проверку; проверьте защиту ветки и права токена.' }
				throw Object.assign( new Error( messages[ response.status ] || 'GitHub недоступен. Повторите проверку.' ), { status: response.status } )
			}
			return response.json()
		}
		async read_optional( repository: string, token: string, revision = 0 ) {
			try { return await this.read( repository, token, revision ) }
			catch( error ) {
				if( ( error as any ).status === 404 ) return null
				throw error
			}
		}
		async create( repository: string, token: string ) {
			const [ owner, name ] = this.$.$shm_hitalama_components_github.repository( repository ).split( '/' )
			const account = await this.api_request( '/users/' + encodeURIComponent( owner ), token )
			let path: string
			if( account.type === 'Organization' ) path = '/orgs/' + encodeURIComponent( owner ) + '/repos'
			else {
				const user = await this.api_request( '/user', token )
				if( String( user.login ).toLowerCase() !== owner.toLowerCase() ) throw new Error( 'Токен принадлежит другому пользователю GitHub. Укажите его имя или доступную организацию.' )
				path = '/user/repos'
			}
			return this.api_request( path, token, 'POST', { name, private: true, auto_init: false } )
		}
		async save( repository: string, token: string, snapshot: string, expected: string | null ): Promise<string> {
			if( expected === null ) {
				const previous = await this.read_optional( repository, token )
				if( previous ) {
					const hashes = await this.hashes( snapshot )
					if( Object.keys( hashes ).every( path => previous.files[ path ] === hashes[ path ] ) ) return previous.head
					throw new Error( 'Репозиторий уже появился на GitHub. Обновите проверку перед сохранением.' )
				}
				await this.create( repository, token )
				const created = await this.read( repository, token )
				return created.head ? this.commit( repository, token, snapshot, created.head ) : this.save( repository, token, snapshot, '' )
			}
			if( expected === '' ) {
				const fresh = await this.read( repository, token )
				if( fresh.head ) throw new Error( 'Репозиторий изменился. Обновите проверку перед сохранением.' )
				const [ path, text ] = Object.entries( JSON.parse( snapshot ) as Record<string, string> )[0] ?? []
				if( !path ) throw new Error( 'Нет файлов для сохранения.' )
				const bytes = new TextEncoder().encode( text )
				let binary = ''
				for( const byte of bytes ) binary += String.fromCharCode( byte )
				await this.request( repository, token, '/contents/' + path.split( '/' ).map( encodeURIComponent ).join( '/' ), 'PUT', { message: 'Initialize application', content: btoa( binary ), branch: fresh.branch } )
				const initialized = await this.read( repository, token )
				return this.commit( repository, token, snapshot, initialized.head )
			}
			return this.commit( repository, token, snapshot, expected )
		}
		async read( repository: string, token: string, revision = 0 ): Promise< $shm_hitalama_components_github_state > {
			const repo = await this.request( repository, token, '' )
			const branch = repo.default_branch || 'main'
			let ref
			try { ref = await this.request( repository, token, '/git/ref/heads/' + encodeURIComponent( branch ) ) }
			catch( error ) {
				if( repo.size === 0 && [ 404, 409 ].includes( ( error as any ).status ) ) return { branch, head: '', tree: '', files: {}, private: repo.private }
				throw error
			}
			const commit = await this.request( repository, token, '/git/commits/' + ref.object.sha )
			const tree = await this.request( repository, token, '/git/trees/' + commit.tree.sha + '?recursive=1' )
			if( tree.truncated ) throw new Error( 'Дерево репозитория слишком большое для полной проверки. Используйте отдельный репозиторий приложения.' )
			return { branch, head: ref.object.sha, tree: commit.tree.sha, files: Object.fromEntries( tree.tree.filter( ( file: any ) => file.type === 'blob' ).map( ( file: any ) => [ file.path, file.sha ] ) ), private: repo.private }
		}
		async hashes( snapshot: string ): Promise< Record< string, string > > {
			const files = JSON.parse( snapshot ) as Record< string, string >, encoder = new TextEncoder()
			return Object.fromEntries( await Promise.all( Object.entries( files ).map( async ( [ path, text ] ) => {
				const body = encoder.encode( text ), header = encoder.encode( 'blob ' + body.length + '\0' )
				const bytes = new Uint8Array( header.length + body.length ); bytes.set( header ); bytes.set( body, header.length )
				const hash = new Uint8Array( await crypto.subtle.digest( 'SHA-1', bytes ) )
				return [ path, Array.from( hash, byte => byte.toString( 16 ).padStart( 2, '0' ) ).join( '' ) ]
			}) ) )
		}
		commit_message( files: Record<string, string> ) {
			return 'Update ' + ( files[ 'module.json' ] ? JSON.parse( files[ 'module.json' ] ).fqn : Object.keys( files ).find( path => path.endsWith( '/module.json' ) )!.replace( /\/module.json$/, '' ) )
		}
		async commit( repository: string, token: string, snapshot: string, expected: string ) {
			const fresh = await this.read( repository, token )
			if( !fresh.head ) throw new Error( 'Репозиторий пуст. Используйте сохранение с инициализацией репозитория.' )
			const { changed, removed } = await this.changes( repository, token, snapshot, fresh )
			// Retrying a request whose response was lost must not make a second commit.
			if( !changed.length && !removed.length ) return fresh.head
			if( fresh.head !== expected ) throw new Error( 'На GitHub появились изменения. Обновите проверку перед коммитом.' )
			const files = JSON.parse( snapshot ) as Record< string, string >
			const tree = await this.request( repository, token, '/git/trees', 'POST', {
				base_tree: fresh.tree,
				tree: [ ...changed.map( path => ({ path, mode: '100644', type: 'blob', content: files[ path ] }) ), ...removed.map( path => ({ path, mode: '100644', type: 'blob', sha: null }) ) ],
			})
			const commit = await this.request( repository, token, '/git/commits', 'POST', {
				message: this.commit_message( files ),
				tree: tree.sha, parents: [ fresh.head ],
			})
			await this.request( repository, token, '/git/refs/heads/' + encodeURIComponent( fresh.branch ), 'PATCH', { sha: commit.sha, force: false } )
			return commit.sha as string
		}
	}
}
